import {DownloadOutlined} from "@ant-design/icons";
import {Alert, Button, message, Table} from "antd";
import type {ColumnsType, TablePaginationConfig} from "antd/es/table";
import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {useExportCSV, useGetCustomers} from "../../../react-query-hooks/customer";
import {StyledPageHeader} from "../../../styles/dashboard/calls/calls";
import {
  StyledDescriptionText,
  StyledNoUpcomingCallBox,
  StyledUpcomingCallsWrapper,
} from "../../calls/upcoming-calls/upcoming-calls.style";
import {FullPageLoader} from "../../full-page-loader";

interface DataType {
  key: string;
  name: string;
  email: string;
  lastPurchaseDate: string;
}

const PAGE_SIZE = 10;

const CustomerDetailsSection = () => {
  const [page, setPage] = useState(1);
  const getCustomersQuery = useGetCustomers(page, PAGE_SIZE);
  const exportCsvQueryMutation = useExportCSV();
  const navigate = useNavigate();

  if (getCustomersQuery.isLoading) {
    return <FullPageLoader />;
  }
  if (getCustomersQuery.isError) {
    return (
      <Alert
        message="Error"
        description={getCustomersQuery.error.message}
        type="error"
        showIcon
      />
    );
  }

  const customersData: DataType[] = getCustomersQuery.data.data.map((transaction) => ({
    key: String(transaction.id),
    name:
      [transaction.first_name, transaction.last_name].filter(Boolean).join(" ") ||
      transaction.email_address,
    email: transaction.email_address.toLowerCase(),
    lastPurchaseDate: transaction.lastPurchaseDate ?? "",
  }));

  const totalCount = getCustomersQuery.data.total;

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (pagination.current && pagination.current !== page) {
      setPage(pagination.current);
    }
  };
  // console.log({customersData});

  const columns: ColumnsType<DataType> = [
    {
      title: "Name",
      dataIndex: "name",
      fixed: "left",
      width: "17.425rem",
      key: "name",
      render: (text) => (
        <>
          <StyledDescriptionText style={{textTransform: "capitalize"}}>
            {text}
          </StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      width: "17.425rem",
      key: "email",
      render: (text) => (
        <>
          <StyledDescriptionText>{text}</StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Last Purchase",
      dataIndex: "lastPurchaseDate",
      width: "17.425rem",
      key: "lastPurchaseDate",
      render: (text: string) => (
        <>
          <StyledDescriptionText>
            {text ? new Date(text).toLocaleDateString() : "-"}
          </StyledDescriptionText>
        </>
      ),
    },
    {
      title: "",
      key: "action",
      render: (_, record) => (
        <Button
          type="link"
          onClick={() =>
            navigate(`/customers/view/${encodeURIComponent(record.key)}`, {
              state: {customerName: record.name},
            })
          }
          size="small"
        >
          <span style={{textDecoration: "underline"}}>View details</span>
        </Button>
      ),
    },
  ];

  const handleOnRow = (record: DataType) => ({
    onClick: () =>
      navigate(`/customers/view/${encodeURIComponent(record.key)}`, {
        state: {customerName: record.name},
      }),
  });

  const handleExportAsCSV = () => {
    exportCsvQueryMutation.mutate(undefined, {
      onSuccess: (data) => {
        message.success("File ready to be downloaded!");
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Coachli.csv");
        document.body.appendChild(link);
        link.click();
      },
    });
  };

  return (
    <>
      <div style={{marginTop: "7rem"}}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
            marginBottom: "4rem",
          }}
        >
          {/* <StyledPageHeader>Customer details 📇</StyledPageHeader> */}
          <StyledPageHeader>Customer details</StyledPageHeader>
          <div>
            <Button
              onClick={handleExportAsCSV}
              disabled={customersData.length < 1}
              block
              icon={<DownloadOutlined style={{fontSize: "1.6rem"}} />}
              type="text"
              size="small"
              loading={exportCsvQueryMutation.isLoading}
            >
              <span style={{textDecoration: "underline"}}>Export CSV</span>
            </Button>
          </div>
        </div>
        {customersData.length < 1 && page === 1 ? (
          <StyledUpcomingCallsWrapper style={{paddingTop: 0}}>
            <StyledNoUpcomingCallBox>
              <p className="info-text">Your customers will appear here</p>
            </StyledNoUpcomingCallBox>
          </StyledUpcomingCallsWrapper>
        ) : (
          <Table
            columns={columns}
            dataSource={customersData}
            loading={getCustomersQuery.isFetching}
            scroll={{x: 100}}
            onChange={handleTableChange}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total: totalCount,
              showSizeChanger: false,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} customers`,
            }}
          />
        )}
      </div>
    </>
  );
};

export {CustomerDetailsSection};
